import {
    getAddress,
    isAddressEqual,
    keccak256,
    stringToHex,
    zeroAddress,
} from 'viem';
import type { Address, Hex } from 'viem';

import {
    OAuthAccountHashInUseError,
    type AccountHashBinding,
    type OAuthAccountBindingResult,
    // the module, not the auth barrel: the barrel's components import this feature.
} from '@/features/auth/accountBinding';
import {
    createBootstrapSessionStatement,
    createBootstrapSessionTypedData,
    randomSessionId,
    type BootstrapSessionTypedData,
    type BootstrapTradingSessionProof,
} from '@/features/tradingSession/bootstrapStatement';
import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type {
    AuthorizationRegistryChallenge,
    AuthorizationRegistryProvision,
    OwnerBindingProof,
    OwnerBindingTypedData,
} from '@/lib/api/authRegistry';
import {
    custodialManagerFor,
    HYPER_EVM_CUSTODIAL_WALLET_MANAGER,
} from '@/lib/config/custodialManagers';
import { eoaBootstrapHash } from '@/lib/eoaBootstrapHash';

export type EoaProvisioningPhase =
    | 'anchor-signing'
    | 'anchor-provisioning'
    | 'hyperevm-signing'
    | 'hyperevm-provisioning';

export interface EoaProvisioningDependencies {
    /** The anchor this account is provisioned on; absent means Cronos. */
    anchorChainId?: AnchorChainId;
    getAuthorizationWallet: (owner: Address) => Promise<Address>;
    getTradingWallet: (owner: Address) => Promise<Address>;
    requestChallenge: (
        owner: Address,
    ) => Promise<AuthorizationRegistryChallenge>;
    signOwnerBinding: (typedData: OwnerBindingTypedData) => Promise<Hex>;
    signBootstrapStatement: (
        typedData: BootstrapSessionTypedData,
    ) => Promise<Hex>;
    provisionAuthorizationWallet: (input: {
        owner: Address;
        ownerBinding: OwnerBindingProof;
        tradingSessionProof: BootstrapTradingSessionProof;
    }) => Promise<AuthorizationRegistryProvision>;
    startTradingWalletCreation: (
        proof: BootstrapTradingSessionProof,
    ) => Promise<void>;
    nowSeconds: () => number;
    newSessionId: () => Hex;
    origin: () => string;
}

export interface EoaProvisioningResult {
    authorizationWallet: Address;
    /** Non-null only when the HyperEVM wallet already existed. */
    tradingWallet: Address | null;
    /** The HyperEVM creation was started; the onboarding status poll reports it landing. */
    tradingWalletCreationStarted: boolean;
}

const CREATE_INTENT_HASH = keccak256(stringToHex('create'));

function normalize(address: Address): Address | null {
    const value = getAddress(address);
    return isAddressEqual(value, zeroAddress) ? null : value;
}

/**
 * Provisions both halves of a Snappy account for a wallet-only user: the anchor
 * authorization wallet (the authorization registry) and the HyperEVM trading
 * wallet. Each leg is skipped when its wallet already exists, so a retry after a
 * failure or a reload resumes rather than restarting.
 */
export async function completeEoaProvisioning(
    ownerInput: string,
    dependencies: EoaProvisioningDependencies,
    onPhase: (phase: EoaProvisioningPhase) => void = () => {},
): Promise<EoaProvisioningResult> {
    const owner = getAddress(ownerInput);
    const anchorChainId = dependencies.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const [existingAuthorization, existingTrading] = await Promise.all([
        dependencies.getAuthorizationWallet(owner).then(normalize),
        dependencies.getTradingWallet(owner).then(normalize),
    ]);

    let authorizationWallet = existingAuthorization;
    if (!authorizationWallet) {
        onPhase('anchor-signing');
        const challenge = await dependencies.requestChallenge(owner);
        assertEoaChallenge(challenge, owner, anchorChainId);
        const signature = await dependencies.signOwnerBinding(
            challenge.typedData,
        );
        const tradingSessionProof = await signBootstrapProof(
            owner,
            custodialManagerFor(anchorChainId),
            dependencies,
            anchorChainId,
        );
        onPhase('anchor-provisioning');
        const provisioned = await dependencies.provisionAuthorizationWallet({
            owner,
            ownerBinding: { ...challenge.ownerBinding, signature },
            tradingSessionProof,
        });
        authorizationWallet = getAddress(provisioned.authorizationWallet);
    }

    if (existingTrading) {
        return {
            authorizationWallet,
            tradingWallet: existingTrading,
            tradingWalletCreationStarted: false,
        };
    }

    onPhase('hyperevm-signing');
    const tradingSessionProof = await signBootstrapProof(
        owner,
        HYPER_EVM_CUSTODIAL_WALLET_MANAGER,
        dependencies,
        anchorChainId,
    );
    onPhase('hyperevm-provisioning');
    await dependencies.startTradingWalletCreation(tradingSessionProof);
    return {
        authorizationWallet,
        tradingWallet: null,
        tradingWalletCreationStarted: true,
    };
}

export interface SocialProvisioningDependencies {
    /** The anchor this account is provisioned on; absent means Cronos. */
    anchorChainId?: AnchorChainId;
    getAuthorizationWallet: (owner: Address) => Promise<Address>;
    getTradingWallet: (owner: Address) => Promise<Address>;
    /** Which owner/wallet the login hash is already registered to, if any. */
    getAccountHashBinding: (
        accountHash: Hex,
    ) => Promise<AccountHashBinding | null>;
    /**
     * Registers the login against the owner's authorization wallet on the
     * anchor, creating that wallet first when the owner has none. One signature
     * (a fresh account) or one transaction (an account this owner already has).
     */
    bindAccountHash: (
        owner: Address,
        accountHash: Hex,
    ) => Promise<OAuthAccountBindingResult>;
    /**
     * Cookie-authorized HyperEVM creation: the live social session IS the
     * authority, so there is no statement to sign and no proof to send.
     */
    startTradingWalletCreation: () => Promise<void>;
}

/**
 * Provisions a Snappy account for a social login that has none, with a connected
 * owner EOA behind it.
 *
 * Same two wallets as the wallet-first ceremony, obtained differently: the
 * anchor leg commits to the pair {social login, owner bootstrap} and is signed
 * by the owner, while the HyperEVM leg is authorized by the social session
 * itself and costs the user nothing. Each leg is skipped when its result already
 * exists, so a retry resumes rather than restarting, and a login another owner
 * already holds stops the run before any wallet is asked to sign.
 */
export async function completeSocialProvisioning(
    ownerInput: string,
    accountHashInput: string,
    dependencies: SocialProvisioningDependencies,
    onPhase: (phase: EoaProvisioningPhase) => void = () => {},
): Promise<EoaProvisioningResult> {
    const owner = getAddress(ownerInput);
    const accountHash = accountHashInput.toLowerCase() as Hex;
    const [existingAuthorization, existingTrading, registered] =
        await Promise.all([
            dependencies.getAuthorizationWallet(owner).then(normalize),
            dependencies.getTradingWallet(owner).then(normalize),
            dependencies.getAccountHashBinding(accountHash),
        ]);
    // a login claimed by somebody else can never be registered here, and the
    // user must find that out before a signature or a gas fee, not after.
    if (registered && !isAddressEqual(registered.owner, owner)) {
        throw new OAuthAccountHashInUseError(
            registered.owner,
            registered.wallet,
        );
    }

    // the anchor leg's whole result is "this login is registered to this owner's
    // wallet", so a run that already achieved it has nothing left to do.
    const alreadyRegistered =
        !!registered &&
        !!existingAuthorization &&
        isAddressEqual(registered.wallet, existingAuthorization);
    let authorizationWallet: Address;
    if (alreadyRegistered) {
        authorizationWallet = existingAuthorization;
    } else {
        onPhase('anchor-signing');
        const bound = await dependencies.bindAccountHash(owner, accountHash);
        authorizationWallet = bound.authorizationWallet;
    }

    if (existingTrading) {
        return {
            authorizationWallet,
            tradingWallet: existingTrading,
            tradingWalletCreationStarted: false,
        };
    }

    // no 'hyperevm-signing' phase: this leg asks the user for nothing.
    onPhase('hyperevm-provisioning');
    await dependencies.startTradingWalletCreation();
    return {
        authorizationWallet,
        tradingWallet: null,
        tradingWalletCreationStarted: true,
    };
}

async function signBootstrapProof(
    owner: Address,
    manager: Address,
    dependencies: EoaProvisioningDependencies,
    anchorChainId: AnchorChainId,
): Promise<BootstrapTradingSessionProof> {
    const statement = createBootstrapSessionStatement({
        owner,
        manager,
        origin: dependencies.origin(),
        issuedAt: dependencies.nowSeconds(),
        sessionId: dependencies.newSessionId(),
    });
    const signature = await dependencies.signBootstrapStatement(
        createBootstrapSessionTypedData(statement, manager, anchorChainId),
    );
    return { method: 'eip712', statement, signature };
}

/**
 * Everything the server could have tampered with, checked before the wallet is
 * ever asked to sign: a challenge minted for anyone else, for any other hash set
 * or for any intent other than `create` must never reach the signing prompt.
 */
function assertEoaChallenge(
    challenge: AuthorizationRegistryChallenge,
    owner: Address,
    anchorChainId: AnchorChainId,
): void {
    const anchorManager = custodialManagerFor(anchorChainId);
    const bootstrapHash = eoaBootstrapHash(owner);
    // eoa-only wallets are created with exactly one hash, so the binding commits
    // to a single-element set rather than the OAuth pair.
    const expectedAccountHashesHash = keccak256(bootstrapHash);
    const message = challenge.typedData.message;
    const matches =
        isAddressEqual(getAddress(challenge.owner), owner) &&
        challenge.accountHash.toLowerCase() === bootstrapHash.toLowerCase() &&
        isAddressEqual(getAddress(message.owner), owner) &&
        challenge.typedData.domain.chainId === anchorChainId &&
        isAddressEqual(
            getAddress(challenge.typedData.domain.verifyingContract),
            anchorManager,
        ) &&
        isAddressEqual(getAddress(message.manager), anchorManager) &&
        message.accountHashesHash.toLowerCase() ===
            expectedAccountHashesHash.toLowerCase() &&
        message.intentHash.toLowerCase() === CREATE_INTENT_HASH.toLowerCase() &&
        message.nonce === challenge.ownerBinding.nonce &&
        message.issuedAt === challenge.ownerBinding.issuedAt &&
        message.expiresAt === challenge.ownerBinding.expiresAt;
    if (!matches) {
        throw new Error(
            'The authorization challenge does not match the connected wallet.',
        );
    }
}

export function createDefaultEoaProvisioningDependencies(
    overrides: Pick<
        EoaProvisioningDependencies,
        | 'anchorChainId'
        | 'getAuthorizationWallet'
        | 'getTradingWallet'
        | 'requestChallenge'
        | 'signOwnerBinding'
        | 'signBootstrapStatement'
        | 'provisionAuthorizationWallet'
        | 'startTradingWalletCreation'
    >,
): EoaProvisioningDependencies {
    return {
        ...overrides,
        nowSeconds: () => Math.floor(Date.now() / 1_000),
        newSessionId: randomSessionId,
        origin: () => window.location.origin,
    };
}
